import { useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { Button, LogoMark } from '../components/ui'
import { requestMagicLink, verifyMagicCode } from '../api/endpoints'
import { setSession } from '../auth/session'

type Step = 'enter-email' | 'enter-code'

export function LoginPage() {
  const [, setLocation] = useLocation()
  const [step, setStep] = useState<Step>('enter-email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleEmailSubmit(e: Event) {
    e.preventDefault()
    if (!email.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await requestMagicLink(email.trim())
      setStep('enter-code')
      setCode('')
    } catch {
      setError('Couldn’t send the code right now. Try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleCodeSubmit(e: Event) {
    e.preventDefault()
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from the email.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await verifyMagicCode(email.trim(), code)
      setSession(res.sessionToken, {
        id: res.operator.id,
        email: res.operator.email,
        name: res.operator.name,
        sites: res.sites,
      })
      setLocation('/conversations')
    } catch {
      setError('That code didn’t work. It may have expired or already been used.')
      setSubmitting(false)
    }
  }

  return (
    <div class="auth-wrap">
      <div class="auth-card">
        <div class="auth-logo">
          <LogoMark size={40} />
          <span style="font-size: 1.05rem">Monty by Momentum</span>
        </div>

        {step === 'enter-email' ? (
          <>
            <div class="auth-title">Sign in</div>
            <p class="auth-sub">
              Enter your email. We’ll send a 6-digit code to sign you in on
              this device — no password to remember.
            </p>

            <form onSubmit={handleEmailSubmit} class="field">
              <label class="field-label" for="email">
                Email address
              </label>
              <input
                id="email"
                class="input"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                disabled={submitting}
                onInput={(e) =>
                  setEmail((e.currentTarget as HTMLInputElement).value)
                }
                required
              />
              {error && (
                <div class="banner banner-error" style={{ marginTop: 8 }}>
                  {error}
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={submitting}
                disabled={!email.trim()}
                style={{ marginTop: 8 }}
              >
                Send sign-in code
              </Button>
            </form>
          </>
        ) : (
          <>
            <div class="auth-title">Enter your code</div>
            <p class="auth-sub">
              We’ve emailed a 6-digit code to <strong>{email}</strong>. It
              expires in 15 minutes.
            </p>

            <form onSubmit={handleCodeSubmit} class="field">
              <label class="field-label" for="code">
                6-digit code
              </label>
              <input
                id="code"
                class="input"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                disabled={submitting}
                onInput={(e) => {
                  const raw = (e.currentTarget as HTMLInputElement).value
                  setCode(raw.replace(/\D/g, '').slice(0, 6))
                }}
                required
                autoFocus
                style={{
                  fontSize: '1.4rem',
                  letterSpacing: '0.4em',
                  textAlign: 'center',
                }}
              />
              {error && (
                <div class="banner banner-error" style={{ marginTop: 8 }}>
                  {error}
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={submitting}
                disabled={code.length !== 6}
                style={{ marginTop: 8 }}
              >
                Sign in
              </Button>
              <Button
                variant="ghost"
                block
                onClick={() => {
                  setStep('enter-email')
                  setCode('')
                  setError(null)
                }}
                style={{ marginTop: 4 }}
              >
                Use a different email
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
